import {
  isTelemetryEnabled,
  shouldUseAppInsightsTransport,
  shouldUseConsoleTransport,
  shouldUseHttpTransport,
} from "./telemetryConfig";
import { TELEMETRY_EVENTS } from "./telemetryEvents";
import { buildTelemetryContext } from "./telemetryContext";
import { shouldDedupeTelemetryEvent } from "./telemetryDeduper";
import { sanitizeTelemetryPayload } from "./telemetrySanitizer";
import { sendConsoleTelemetryEvent } from "./transports/consoleTransport";
import { sendHttpTelemetryEvent } from "./transports/httpTransport";

const listeners = new Set();

function sendToAppInsights(event) {
  import("./transports/appInsightsTransport")
    .then(({ sendAppInsightsTelemetryEvent }) => {
      sendAppInsightsTelemetryEvent(event);
    })
    .catch(() => {
      // Ignore transport failures.
    });
}

function sendAppInsightsException(error, properties) {
  import("./transports/appInsightsTransport")
    .then(({ trackAppInsightsError }) => {
      trackAppInsightsError(error, properties);
    })
    .catch(() => {
      // Ignore transport failures.
    });
}

export function subscribeTelemetry(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function trackEvent(eventName, payload = {}, options = {}) {
  if (!isTelemetryEnabled()) {
    return null;
  }

  try {
    const dedupeKey = options.dedupeKey ?? payload.dedupeKey;
    if (shouldDedupeTelemetryEvent(eventName, dedupeKey)) {
      return null;
    }

    const event = {
      name: eventName,
      category: options.category ?? payload.category ?? "interaction",
      timestamp: new Date().toISOString(),
      ...buildTelemetryContext(options.context),
      ...sanitizeTelemetryPayload(payload),
    };

    if (shouldUseConsoleTransport()) {
      try {
        sendConsoleTelemetryEvent(event);
      } catch {
        // Ignore transport failures.
      }
    }

    if (shouldUseHttpTransport()) {
      try {
        sendHttpTelemetryEvent(event);
      } catch {
        // Ignore transport failures.
      }
    }

    if (shouldUseAppInsightsTransport()) {
      sendToAppInsights(event);
    }

    for (const listener of listeners) {
      try {
        listener(event);
      } catch {
        // Ignore subscriber failures.
      }
    }

    return event;
  } catch {
    return null;
  }
}

export function trackPerformanceMetric(metric, payload = {}, options = {}) {
  return trackEvent(
    options.eventName ?? TELEMETRY_EVENTS.PERFORMANCE_MEASURE,
    {
      metric,
      ...payload,
    },
    {
      ...options,
      category: "performance",
    },
  );
}

export function trackError(error, payload = {}, options = {}) {
  const normalizedError =
    error instanceof Error
      ? {
          errorName: error.name,
          errorMessage: error.message,
        }
      : {
          errorMessage: String(error),
        };

  if (shouldUseAppInsightsTransport()) {
    sendAppInsightsException(error, {
      ...normalizedError,
      ...sanitizeTelemetryPayload(payload),
    });
  }

  return trackEvent(
    options.eventName ?? TELEMETRY_EVENTS.APPLICATION_ERROR,
    {
      ...normalizedError,
      ...payload,
    },
    {
      ...options,
      category: "error",
    },
  );
}
